/**
 * Lightweight Capacitor bridge.
 *
 * The Zcardon web build is the source of truth. We wrap *only* the parts of
 * Capacitor that we actually use so any future plugin add doesn't require
 * touching dozens of files. Everything here is no-op safe in the browser —
 * `Capacitor.isNativePlatform()` returns `false` and the helpers do nothing.
 */
import { Capacitor } from "@capacitor/core";

export const isNative = () => {
    try { return Capacitor.isNativePlatform(); } catch { return false; }
};

export const platform = () => {
    try { return Capacitor.getPlatform(); } catch { return "web"; }
};

/**
 * Light haptic ping on success actions (card issued, payment confirmed, etc.).
 * On the web this is a no-op; on iOS it taps the Taptic Engine.
 */
export async function tap(style = "light") {
    if (!isNative()) return;
    try {
        const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
        const map = { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy };
        await Haptics.impact({ style: map[style] || ImpactStyle.Light });
    } catch { /* fail silently */ }
}

/** Hide the iOS soft keyboard programmatically (after submit). */
export async function dismissKeyboard() {
    if (!isNative()) return;
    try {
        const { Keyboard } = await import("@capacitor/keyboard");
        await Keyboard.hide();
    } catch { /* noop */ }
}

/**
 * Configure the iOS status bar to match Zcardon's dark green.
 * Called once on app boot from a small effect in `App.js`.
 */
export async function tintStatusBar() {
    if (!isNative()) return;
    try {
        const { StatusBar, Style } = await import("@capacitor/status-bar");
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: "#112318" });
    } catch { /* noop */ }
}
